<template>
  <div class="patrol-wrap">

    <!-- ── Top Bar ── -->
    <div class="patrol-topbar">
      <div>
        <h2 class="patrol-title" :style="{ color: brandColor }">Security Patrol Logs</h2>
        <p class="patrol-subtitle">View and monitor security guard patrol activity</p>
      </div>
      <div class="topbar-actions">
        <PremiumSelect
          v-model="filterGuard"
          :items="guardItems"
          item-text="text"
          item-value="value"
          placeholder="All Guards"
          icon="mdi-shield-account-outline"
          style="min-width:170px"
          @input="onFilterChange"
        />
        <PremiumSelect
          v-model="filterArea"
          :items="areaItems"
          item-text="text"
          item-value="value"
          placeholder="All Checkpoints"
          icon="mdi-map-marker-outline"
          style="min-width:170px"
          @input="onFilterChange"
        />
        <button class="pt-add-btn" :style="{ background: brandColor }" @click="qrModal = true" type="button">
          <v-icon size="16" color="white">mdi-qrcode-scan</v-icon>
          <span>Checkpoints</span>
        </button>
      </div>
    </div>

    <!-- ── Main Two-Column Layout ── -->
    <div class="main-layout">

      <!-- ══ LEFT: Calendar ══ -->
      <div class="cal-panel">
        <!-- Month Navigation -->
        <div class="cal-header">
          <button class="cal-nav-btn" @click="prevMonth" type="button">
            <v-icon size="20">mdi-chevron-left</v-icon>
          </button>
          <span class="cal-month-label">{{ calMonthLabel }}</span>
          <button class="cal-nav-btn" @click="nextMonth" type="button">
            <v-icon size="20">mdi-chevron-right</v-icon>
          </button>
        </div>

        <!-- Day of week headers -->
        <div class="cal-grid">
          <div class="cal-day-hdr" v-for="d in ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']" :key="d">{{ d }}</div>
        </div>

        <div class="cal-grid-divider" />

        <!-- Day cells grid -->
        <div class="cal-cells-grid">
          <!-- Leading empty cells -->
          <div v-for="e in calStartPad" :key="'e'+e" class="cal-cell cal-empty-cell" />

          <!-- Day cells -->
          <div
            v-for="day in calDaysInMonth"
            :key="day"
            class="cal-cell"
            :class="{
              'is-today':    isToday(day),
              'is-selected': selectedDate === calDateStr(day),
              'has-logs':    calLogCount(day) > 0,
            }"
            @click="selectDate(day)"
          >
            <span class="day-num" :style="selectedDate === calDateStr(day) ? { background: brandColor, color: '#fff' } : {}">
              {{ day }}
            </span>
            <span v-if="calLogCount(day) > 0" class="day-badge" :style="{ background: brandColor }">
              {{ calLogCount(day) }}
            </span>
          </div>
        </div>

        <!-- Selected date label -->
        <div class="cal-selected-label" v-if="selectedDate">
          <v-icon size="14" color="#94A3B8" class="mr-1">mdi-calendar-check</v-icon>
          {{ formatShortDate(selectedDate) }}
          <button class="cal-clear-date" @click="selectedDate = null; fetchLogs()" type="button" title="Show all month">
            <v-icon size="13">mdi-close</v-icon>
          </button>
        </div>
      </div>

      <!-- ══ RIGHT: Log List ══ -->
      <div class="list-panel">
        <!-- Loading -->
        <div v-if="loading" class="list-loading">
          <v-progress-circular indeterminate :color="brandColor" size="28" />
        </div>

        <!-- Empty -->
        <div v-else-if="logs.length === 0" class="list-empty">
          <v-icon size="40" color="#CBD5E1">mdi-shield-search</v-icon>
          <p>No patrol scans found</p>
          <span>{{ selectedDate ? formatShortDate(selectedDate) : calMonthLabel }}</span>
        </div>

        <!-- Log cards -->
        <div v-else class="log-list">
          <div
            v-for="item in logs"
            :key="item._id"
            class="log-card"
          >
            <div class="log-avatar" :style="{ background: brandColor + '18', color: brandColor }">
              <v-icon size="20" :color="brandColor">mdi-walk</v-icon>
            </div>
            <div class="log-info">
              <div class="log-name">{{ item.guard_id ? item.guard_id.name : 'Unknown' }}</div>
              <div class="log-area">
                <v-icon size="12" color="#94A3B8" class="mr-1">mdi-map-marker-outline</v-icon>
                {{ item.area_name || '—' }}
              </div>
            </div>
            <div class="log-time">
              <span class="log-datetime">
                {{ formatCalDay(item.scanned_at) }}
                <span class="log-time-dot">·</span>
                <span class="log-hour" :style="{ color: brandColor }">{{ formatTime(item.scanned_at) }}</span>
              </span>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalItems > itemsPerPage" class="list-pagination">
          <button
            class="pag-btn"
            :disabled="page <= 1"
            @click="page--; fetchLogs()"
            type="button"
          ><v-icon size="16">mdi-chevron-left</v-icon></button>
          <span class="pag-info">{{ page }} / {{ totalPages }}</span>
          <button
            class="pag-btn"
            :disabled="page >= totalPages"
            @click="page++; fetchLogs()"
            type="button"
          ><v-icon size="16">mdi-chevron-right</v-icon></button>
        </div>
      </div>

    </div><!-- /main-layout -->

    <!-- ══ QR Codes Modal ══ -->
    <PremiumModal
      v-model="qrModal"
      title="Manage Checkpoints"
      max-width="820"
      submit-text=""
      cancel-text="Close"
      @cancel="qrModal = false"
      @close="qrModal = false"
    >
      <template #footer-extra>
        <button class="pt-add-btn ml-auto" :style="{ background: brandColor }" @click="openCreateQrForm" type="button">
          <v-icon size="16" color="white">mdi-plus</v-icon>
          <span>Add Checkpoint</span>
        </button>
      </template>
      <template #default>
        <div class="qr-modal-body">

          <!-- Loading -->
          <div v-if="qrLoading" class="qr-state-box">
            <v-progress-circular indeterminate :color="brandColor" size="32" />
          </div>

          <!-- Empty state -->
          <div v-else-if="!qrCodes.length" class="qr-state-box">
            <div class="qr-empty-icon">
              <v-icon size="36" color="#CBD5E1">mdi-qrcode-scan</v-icon>
            </div>
            <p class="qr-empty-title">No Checkpoints yet</p>
            <span class="qr-empty-sub">Add a checkpoint for each patrol area</span>
          </div>

          <!-- QR list table -->
          <div v-else class="qr-list">
            <div class="qr-list-header">
              <span class="qr-col qr-col-no">No.</span>
              <span class="qr-col qr-col-area">Area Name</span>
              <span class="qr-col qr-col-scans">Total Scans</span>
              <span class="qr-col qr-col-date">Created Date</span>
              <span class="qr-col qr-col-status">Status</span>
              <span class="qr-col qr-col-actions">Actions</span>
            </div>
            <div v-for="(item, idx) in qrCodes" :key="item._id" class="qr-list-row">
              <span class="qr-col qr-col-no">
                <span class="qr-no-badge">{{ idx + 1 }}</span>
              </span>
              <span class="qr-col qr-col-area">{{ item.area_name }}</span>
              <span class="qr-col qr-col-scans">{{ item.total_scans || 0 }}</span>
              <span class="qr-col qr-col-date">{{ formatShortDate(item.created_at) }}</span>
              <span class="qr-col qr-col-status">
                <div class="status-toggle-wrap">
              
                  <v-switch :input-value="item.status === 'active' || item.status === true" :color="brandColor" dense hide-details class="qr-toggle ml-2" @change="toggleQrStatus(item)" />
                </div>
              </span>
              <span class="qr-col qr-col-actions">
                <div class="action-group">
                  <v-tooltip top :open-delay="200" content-class="action-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <button type="button" class="action-btn"  v-bind="attrs" v-on="on" @click="downloadQr(item._id, 'pdf')">
                        <v-icon size="19" color="#10b981">mdi-tray-arrow-down</v-icon>
                         <div class="hover-bg"></div>
                      </button>
                    </template>
                    <span>Download PDF</span>
                  </v-tooltip>
         
                  <v-tooltip top :open-delay="200" content-class="action-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <button type="button" class="action-btn" v-bind="attrs" v-on="on" @click="editQr(item)">
                      <v-icon size="19" color="#3B82F6">mdi-pencil-outline</v-icon>
                        <div class="hover-bg"></div>
                      </button>
                    </template>
                    <span>Edit</span>
                  </v-tooltip>
                  <v-tooltip top :open-delay="200" content-class="action-tooltip">
                    <template v-slot:activator="{ on, attrs }">
                      <button type="button" class="action-btn" v-bind="attrs" v-on="on" @click="confirmDeleteQr(item)">
                        <v-icon size="19" color="#EF4444">mdi-trash-can-outline</v-icon>
                        <div class="hover-bg"></div>
                      </button>
                    </template>
                    <span>Delete</span>
                  </v-tooltip>
                </div>
              </span>
            </div>
          </div>


        </div>
      </template>
    </PremiumModal>

    <!-- ══ Create/Edit QR Modal ══ -->
    <PremiumModal
      v-model="formModal"
      :title="formMode === 'create' ? 'Add Checkpoint' : 'Edit Checkpoint'"
      max-width="480"
      :submit-text="timeSlotDialog ? (timePickerStep === 'from' ? 'Next' : 'Add Slot') : (formMode === 'create' ? 'Generate' : 'Update')"
      :cancel-text="timeSlotDialog ? 'Back' : 'Cancel'"
      :loading="saving"
      @submit="timeSlotDialog ? confirmTimePickerStep() : saveQr()"
      @cancel="timeSlotDialog ? cancelTimeSlot() : (formModal = false)"
      @close="formModal = false"
    >
      <div v-if="!timeSlotDialog" style="display:flex;flex-direction:column;gap:18px;">
        <PremiumInput
          v-model="formData.area_name"
          label="Checkpoint"
          placeholder="e.g. Gate 1, Parking, Block A"
          icon="mdi-map-marker-outline"
          v-validate="'required'"
          name="area_name"
          data-vv-as="Area Name"
          :error="errors.first('area_name')"
        />
        <div>
          <div class="section-label mb-3">Time Slots <span class="section-label-hint">Optional</span></div>
          <div v-for="(slot, idx) in formData.time_slots" :key="idx" class="time-slot-row">
            <v-icon color="grey" size="18" class="mr-2">mdi-clock-outline</v-icon>
            <span class="slot-text">{{ formatTime12hr(slot.from) }} – {{ formatTime12hr(slot.to) }}</span>
            <v-spacer />
            <button class="slot-remove-btn" @click="removeTimeSlot(idx)" type="button">
              <v-icon size="16">mdi-close</v-icon>
            </button>
          </div>
          <button class="add-slot-btn" @click="openTimeSlotPicker" type="button">
            <v-icon size="16" class="mr-1">mdi-plus</v-icon>Add Time Slot
          </button>
        </div>
      </div>
      <div v-else style="display:flex;flex-direction:column;gap:12px;">
        <div class="inline-picker-header">
          <v-icon size="18" color="grey" class="mr-2">mdi-clock-outline</v-icon>
          <span class="inline-picker-title">{{ timePickerStep === 'from' ? 'Select Start Time' : 'Select End Time' }}</span>
        </div>
        <div v-if="timePickerStep === 'to' && pendingFromTime" class="inline-picker-hint">
          Start time: <strong>{{ formatTime12hr(pendingFromTime) }}</strong>
        </div>
        <div class="inline-picker-wrap">
          <v-time-picker :key="timePickerStep" v-model="timePickerValue" full-width scrollable ampm-in-title :color="brandColor" class="inline-time-picker" />
        </div>
      </div>
    </PremiumModal>

    <!-- ══ Delete QR Confirmation ══ -->
    <ConfirmModal
      v-model="deleteQrDialog"
      type="danger"
      title="Delete QR Code"
      message="Are you sure you want to delete this QR code? This action cannot be undone."
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteQrLoading"
      @confirm="deleteQr"
    />

  </div>
</template>

<script>
import moment from "moment";
import { mapState, mapMutations } from "vuex";
import PremiumModal from "../../common/PremiumModal.vue";
import PremiumInput from "../../common/PremiumInput.vue";
import PremiumSelect from "../../common/PremiumSelect.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";

export default {
  name: "PatrolLogs",
  components: { PremiumModal, PremiumInput, PremiumSelect, ConfirmModal },

  data() {
    return {
      // Filters
      filterGuard: null,
      filterArea:  null,
      guards: [],
      areas:  [],

      // Calendar state
      calYear:  new Date().getFullYear(),
      calMonth: new Date().getMonth(),  // 0-indexed
      calLogs:  [],                     // full month logs for badges
      selectedDate: null,               // 'YYYY-MM-DD' or null = show all month

      // List state
      loading:     false,
      logs:        [],
      page:        1,
      itemsPerPage: 20,
      totalItems:  0,

      // QR Modal
      qrModal:      false,
      qrLoading:    false,
      qrCodes:      [],
      qrTotalItems: 0,
      qrColumns: [
        { key: 'created_at',  label: 'Created Date', width: '130px' },
        { key: 'area_name',   label: 'Area Name'                    },
        { key: 'total_scans', label: 'Total Scans',  width: '110px' },
        { key: 'status',      label: 'Status',       width: '110px' },
      ],

      // Form modal
      formModal: false,
      formMode:  'create',
      saving:    false,
      formData:  { id: null, area_name: '', time_slots: [] },

      // Time slot picker
      timeSlotDialog:  false,
      timePickerStep:  'from',
      timePickerValue: null,
      pendingFromTime: null,

      // Delete QR confirm
      deleteQrDialog:  false,
      deleteQrItem:    null,
      deleteQrLoading: false,
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor", "buttonColor"]),
    brandColor() {
      return this.buttonColor || this.primaryColor || "#30384d";
    },

    guardItems() {
      return [
        { text: 'All Guards', value: null },
        ...this.guards.map(g => ({ text: g.name, value: g._id || g.id })),
      ];
    },
    areaItems() {
      return [
        { text: 'All Checkpoints', value: null },
        ...this.areas.map(a => ({ text: a, value: a })),
      ];
    },

    // ── Calendar ──
    calMonthLabel() {
      return moment({ year: this.calYear, month: this.calMonth }).format('MMMM YYYY');
    },
    calDaysInMonth() {
      return moment({ year: this.calYear, month: this.calMonth }).daysInMonth();
    },
    calStartPad() {
      return moment({ year: this.calYear, month: this.calMonth, day: 1 }).day();
    },
    calCountMap() {
      const map = {};
      this.calLogs.forEach(l => {
        const d = moment(l.scanned_at).format('YYYY-MM-DD');
        map[d] = (map[d] || 0) + 1;
      });
      return map;
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.itemsPerPage));
    },
  },

  watch: {
    qrModal(val) {
      if (val) this.fetchQrCodes();
    },
  },

  mounted() {
    this.fetchGuards();
    this.fetchCalendarLogs();  // loads full month → badges + list
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    // ── Filter change ────────────────────────────
    onFilterChange() {
      this.page = 1;
      this.fetchCalendarLogs();
    },

    // ── Calendar navigation ──────────────────────
    prevMonth() {
      const m = moment({ year: this.calYear, month: this.calMonth }).subtract(1, 'month');
      this.calYear  = m.year();
      this.calMonth = m.month();
      this.selectedDate = null;
      this.page = 1;
      this.fetchCalendarLogs();
    },
    nextMonth() {
      const m = moment({ year: this.calYear, month: this.calMonth }).add(1, 'month');
      this.calYear  = m.year();
      this.calMonth = m.month();
      this.selectedDate = null;
      this.page = 1;
      this.fetchCalendarLogs();
    },
    selectDate(day) {
      const d = this.calDateStr(day);
      this.selectedDate = this.selectedDate === d ? null : d;
      this.page = 1;
      this.fetchLogs();
    },
    calDateStr(day) {
      return moment({ year: this.calYear, month: this.calMonth, day }).format('YYYY-MM-DD');
    },
    calLogCount(day) {
      return this.calCountMap[this.calDateStr(day)] || 0;
    },
    isToday(day) {
      return this.calDateStr(day) === moment().format('YYYY-MM-DD');
    },

    // ── Fetch full month (for calendar badges) ───
    async fetchCalendarLogs() {
      try {
        const params = {
          status:  'success',
          month:   this.calMonth + 1,
          year:    this.calYear,
          perPage: 500,
        };
        if (this.filterGuard) params.guard_id  = this.filterGuard;
        if (this.filterArea)  params.area_name = this.filterArea;
        const res = await this.$axios.get("societyadmin/patrol-scan-logs", { params });
        if (res.data.status) {
          this.calLogs = res.data.logs;
        }
      } catch (err) {
        console.error("fetchCalendarLogs error", err);
      }
      // Also load the list for current selection
      this.fetchLogs();
    },

    // ── Fetch list (right panel) ─────────────────
    async fetchLogs() {
      this.loading = true;
      try {
        const params = { status: 'success', page: this.page, perPage: this.itemsPerPage };

        if (this.selectedDate) {
          // Specific day clicked on calendar
          params.date = this.selectedDate;
        } else {
          // Full month
          params.month = this.calMonth + 1;
          params.year  = this.calYear;
        }

        if (this.filterGuard) params.guard_id  = this.filterGuard;
        if (this.filterArea)  params.area_name = this.filterArea;

        const res = await this.$axios.get("societyadmin/patrol-scan-logs", { params });
        if (res.data.status) {
          this.logs       = res.data.logs;
          this.totalItems = res.data.total || res.data.logs.length;
        }
      } catch (err) {
        console.error("fetchLogs error", err);
      } finally {
        this.loading = false;
      }
    },

    // ── Guards & Areas ───────────────────────────
    async fetchGuards() {
      try {
        const res = await this.$axios.get("societyadmin/securityguard?pagination=false");
        if (res.data.status) {
          this.guards = res.data.securityguards || res.data.members || res.data.data || [];
        }
      } catch (err) {
        console.error("fetchGuards error", err);
      }
    },
    async fetchQrCodes() {
      this.qrLoading = true;
      try {
        const res = await this.$axios.get("societyadmin/patrol-qr");
        if (res.data.status) {
          this.qrCodes      = res.data.qrs || [];
          this.qrTotalItems = this.qrCodes.length;
          this.areas = [...new Set(this.qrCodes.map(q => q.area_name).filter(Boolean))];
        }
      } catch (err) {
        console.error("fetchQrCodes error", err);
      } finally {
        this.qrLoading = false;
      }
    },

    // ── QR CRUD ──────────────────────────────────
    openCreateQrForm() {
      this.formMode = 'create';
      this.formData = { id: null, area_name: '', time_slots: [] };
      this.timeSlotDialog = false; this.timePickerValue = null;
      this.pendingFromTime = null; this.timePickerStep = 'from';
      this.$validator.reset();
      this.formModal = true;
    },
    editQr(item) {
      this.formMode = 'edit';
      this.formData = { id: item._id, area_name: item.area_name, time_slots: item.time_slots ? [...item.time_slots] : [] };
      this.timeSlotDialog = false; this.timePickerValue = null;
      this.pendingFromTime = null; this.timePickerStep = 'from';
      this.$validator.reset();
      this.formModal = true;
    },
    async saveQr() {
      const valid = await this.$validator.validateAll();
      if (!valid) return;
      this.saving = true;
      try {
        const payload = { area_name: this.formData.area_name, time_slots: this.formData.time_slots };
        const res = this.formMode === 'create'
          ? await this.$axios.post("societyadmin/patrol-qr", payload)
          : await this.$axios.put(`societyadmin/patrol-qr/${this.formData.id}`, payload);
        if (res.data.status) {
          this.setAlert({ showAlert: true, message: res.data.message, variant: "green accent-4", timeout: 3000 });
          this.formModal = false;
          this.fetchQrCodes();
        } else {
          this.setAlert({ showAlert: true, message: res.data.message, variant: "red accent-3", timeout: 3000 });
        }
      } catch (err) {
        this.setAlert({ showAlert: true, message: `Failed to ${this.formMode} QR`, variant: "red accent-3", timeout: 3000 });
      } finally {
        this.saving = false;
      }
    },
    async toggleQrStatus(item) {
      try {
        const isActive = item.status === 'active' || item.status === true;
        const newStatus = !isActive;   // true = active, false = inactive
        const res = await this.$axios.put(`societyadmin/patrol-qr/${item._id}/status`, { status: newStatus });
        if (res.data.status) {
          this.setAlert({ showAlert: true, message: "Status updated", variant: "green accent-4", timeout: 3000 });
          this.fetchQrCodes();
        } else {
          this.setAlert({ showAlert: true, message: res.data.message, variant: "red accent-3", timeout: 3000 });
        }
      } catch (err) {
        this.setAlert({ showAlert: true, message: "Failed to update status", variant: "red accent-3", timeout: 3000 });
      }
    },
    confirmDeleteQr(item) {
      this.deleteQrItem   = item;
      this.deleteQrDialog = true;
    },

    deleteQr() {
      if (!this.deleteQrItem) return;
      this.deleteQrLoading = true;
      this.$axios.delete(`societyadmin/patrol-qr/${this.deleteQrItem._id}`)
        .then(res => {
          this.deleteQrLoading = false;
          if (res.data.status) {
            this.setAlert({ showAlert: true, message: res.data.message || "QR deleted", variant: "green accent-4", timeout: 3000 });
            this.deleteQrDialog = false;
            this.deleteQrItem   = null;
            this.fetchQrCodes();
          } else {
            this.setAlert({ showAlert: true, message: res.data.message, variant: "red accent-3", timeout: 3000 });
          }
        })
        .catch(() => {
          this.deleteQrLoading = false;
          this.setAlert({ showAlert: true, message: "Something went wrong", variant: "red accent-3", timeout: 3000 });
        });
    },
    downloadQr(id, type) {
      this.$axios.get(`societyadmin/patrol-qr/${id}/download?type=${type}`, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const a = document.createElement('a');
          a.href = url;
          a.setAttribute('download', `Patrol_QR_${id}.${type}`);
          document.body.appendChild(a);
          a.click();
          a.remove();
        })
        .catch(() => this.setAlert({ showAlert: true, message: "Failed to download", variant: "red accent-3", timeout: 3000 }));
    },

    // ── Time slot picker ─────────────────────────
    openTimeSlotPicker() {
      this.timePickerStep = 'from'; this.timePickerValue = null;
      this.pendingFromTime = null;  this.timeSlotDialog = true;
    },
    cancelTimeSlot() {
      if (this.timePickerStep === 'to') {
        this.timePickerStep = 'from'; this.timePickerValue = null; this.pendingFromTime = null;
      } else {
        this.timeSlotDialog = false; this.timePickerValue = null;
        this.pendingFromTime = null; this.timePickerStep = 'from';
      }
    },
    confirmTimePickerStep() {
      if (!this.timePickerValue) return;
      if (this.timePickerStep === 'from') {
        this.pendingFromTime = this.timePickerValue;
        this.timePickerValue = null;
        this.timePickerStep  = 'to';
      } else {
        const toMin = t => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
        const diff = toMin(this.timePickerValue) >= toMin(this.pendingFromTime)
          ? toMin(this.timePickerValue) - toMin(this.pendingFromTime)
          : 1440 - toMin(this.pendingFromTime) + toMin(this.timePickerValue);
        if (diff <= 0)   { this.setAlert({ showAlert: true, message: 'End time must be after start time', variant: "red accent-3", timeout: 3000 }); return; }
        if (diff > 120)  { this.setAlert({ showAlert: true, message: 'Time slot cannot exceed 2 hours',  variant: "red accent-3", timeout: 3000 }); return; }
        this.formData.time_slots.push({ from: this.pendingFromTime, to: this.timePickerValue });
        this.timeSlotDialog = false; this.timePickerValue = null;
        this.pendingFromTime = null; this.timePickerStep = 'from';
      }
    },
    removeTimeSlot(idx) { this.formData.time_slots.splice(idx, 1); },

    // ── Formatters ───────────────────────────────
    formatTime12hr(time) {
      if (!time) return '';
      const [h, m] = time.split(':').map(Number);
      return `${String(h % 12 || 12).padStart(2,'0')}:${String(m).padStart(2,'0')} ${h >= 12 ? 'PM' : 'AM'}`;
    },
    formatShortDate(d) { return d ? moment(d).format('DD MMM YYYY') : '—'; },
    formatCalDay(d)    { return d ? moment(d).format('DD MMM, YYYY') : '—'; },
    formatTime(d)      { return d ? moment(d).format('hh:mm A') : '—'; },
  },
};
</script>

<style scoped>
/* ── Page wrapper ── */
.patrol-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Top bar ── */
.patrol-topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.patrol-title {
  font-size: 20px;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
}
.patrol-subtitle {
  font-size: 12.5px;
  color: var(--tx3, #9ca3af);
  margin: 2px 0 0;
}
.topbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

/* ── Two-column layout ── */
.main-layout {
  display: grid;
  grid-template-columns: 30% 70%;
  gap: 20px;
  align-items: start;
  height: calc(100vh - 180px);
}
@media (max-width: 960px) {
  .main-layout { grid-template-columns: 1fr; height: auto; }
}

/* ══ Calendar panel ══ */
.cal-panel {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04);
}

.cal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
}
.cal-month-label {
  font-family: "Bricolage Grotesque", sans-serif;
  font-size: 17px;
  font-weight: 800;
  color: var(--tx, #111827);
  letter-spacing: -0.02em;
}
.cal-nav-btn {
  width: 34px; height: 34px;
  border-radius: 10px;
  border: 1.5px solid var(--border, #e2e8f0);
  background: var(--surface2, #f8fafc);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s;
  color: var(--tx2, #374151);
}
.cal-nav-btn:hover {
  background: var(--surface, #fff);
  border-color: #94a3b8;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
}

/* Day-of-week header row */
.cal-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 0 8px;
  gap: 2px;
}
.cal-day-hdr {
  padding: 8px 0 10px;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  color: var(--tx3, #9ca3af);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.cal-grid-divider {
  height: 1px;
  background: var(--border2, #f1f5f9);
  margin: 0 12px;
}
.cal-cells-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding: 6px 8px 8px;
  gap: 2px;
}

/* Day cells */
.cal-cell {
  aspect-ratio: 1;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  transition: background 0.15s, transform 0.12s;
  position: relative;
}
.cal-cell:hover:not(.cal-empty-cell) {
  background: var(--surface2, #f1f5f9);
  transform: scale(1.06);
}
.cal-empty-cell { cursor: default; }
.cal-empty-cell:hover { background: transparent; transform: none; }

/* Day number circle */
.day-num {
  width: 34px; height: 34px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--tx2, #374151);
  transition: all 0.18s;
  line-height: 1;
}
.has-logs .day-num  { font-weight: 700; color: var(--tx, #111827); }
.is-today .day-num  {
  font-weight: 800;
  border: 2px solid var(--border, #e2e8f0);
}
.is-selected .day-num {
  font-weight: 800;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
}

/* Scan badge dot */
.day-badge {
  font-size: 9.5px;
  font-weight: 800;
  color: #fff;
  padding: 1px 5px;
  border-radius: 8px;
  min-width: 18px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  letter-spacing: 0.02em;
}

/* Selected date info strip */
.cal-selected-label {
  display: flex;
  align-items: center;
  margin: 8px 12px 12px;
  padding: 10px 14px;
  background: var(--surface2, #f8fafc);
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #374151);
  gap: 6px;
  border: 1px solid var(--border2, #f1f5f9);
}
.cal-clear-date {
  margin-left: auto;
  width: 22px; height: 22px;
  border-radius: 50%;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--surface, #fff);
  color: var(--tx3, #6b7280);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s;
}
.cal-clear-date:hover {
  background: #fef2f2;
  border-color: #fca5a5;
  color: #ef4444;
}

/* ══ List panel ══ */
.list-panel {
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07), 0 1px 3px rgba(0,0,0,0.04);
  display: flex;
  flex-direction: column;
  height: calc(100vh - 180px);
  overflow: hidden;
}
.list-loading {
  flex: 1;
  display: flex; align-items: center; justify-content: center;
  padding: 48px;
}
.list-empty {
  flex: 1;
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 8px;
  padding: 48px 24px;
  color: var(--tx3, #9ca3af);
  font-size: 13px;
}
.list-empty p { margin: 0; font-weight: 600; }
.list-empty span { font-size: 12px; }

.log-list {
  flex: 1;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  /* custom scrollbar */
  scrollbar-width: thin;
  scrollbar-color: var(--border, #e2e8f0) transparent;
}
.log-list::-webkit-scrollbar { width: 5px; }
.log-list::-webkit-scrollbar-track { background: transparent; }
.log-list::-webkit-scrollbar-thumb { background: var(--border, #e2e8f0); border-radius: 10px; }
.log-list::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
.log-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid var(--border2, #f1f5f9);
  background: var(--surface2, #f8fafc);
  transition: box-shadow 0.15s;
}
.log-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
.log-avatar {
  width: 38px; height: 38px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.log-info { flex: 1; min-width: 0; }
.log-name { font-size: 13.5px; font-weight: 700; color: var(--tx, #111827); }
.log-area {
  display: flex; align-items: center;
  font-size: 12px; color: var(--tx3, #6b7280); margin-top: 2px;
}
.log-time {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}
.log-datetime {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx2, #374151);
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
}
.log-time-dot {
  color: var(--tx3, #9ca3af);
  font-weight: 400;
}
.log-hour {
  font-weight: 800;
}

/* Pagination */
.list-pagination {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid var(--border2, #f1f5f9);
}
.pag-btn {
  width: 30px; height: 30px; border-radius: 8px;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--surface2, #f8fafc);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.18s;
}
.pag-btn:disabled { opacity: 0.4; cursor: default; }
.pag-btn:not(:disabled):hover { background: #f1f5f9; border-color: #cbd5e1; }
.pag-info { font-size: 12.5px; font-weight: 600; color: var(--tx3); }

/* ── QR status toggle ── */
.status-toggle-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
}
.qr-toggle {
  margin: 0 !important;
  padding: 0 !important;
}
.qr-toggle >>> .v-input__slot { margin: 0 !important; }

/* ── Status chips ── */
.status-chip {
  display: inline-flex; align-items: center;
  padding: 3px 10px; border-radius: 20px;
  font-size: 10.5px; font-weight: 800;
  letter-spacing: 0.05em; text-transform: uppercase;
}
.status-active   { background: rgba(16,185,129,0.12); color: #059669; }
.status-inactive { background: rgba(239,68,68,0.12);  color: #DC2626; }

/* ── QR download button (filled rounded) ── */
.qr-dl-btn {
  width: 32px;
  height: 32px;
  border-radius: 10px;
  border: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}
.qr-dl-btn:hover {
  filter: brightness(1.1);
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(0,0,0,0.2);
}

/* ── QR modal ── */
.qr-modal-body { display: flex; flex-direction: column; gap: 16px; }

/* State boxes (loading / empty) */
.qr-state-box {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 10px; padding: 48px 24px;
  border: 1px solid var(--border2, #f1f5f9);
  border-radius: 14px;
  background: var(--surface2, #f8fafc);
}
.qr-empty-icon {
  width: 60px; height: 60px; border-radius: 50%;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  display: flex; align-items: center; justify-content: center;
}
.qr-empty-title { font-size: 15px; font-weight: 700; color: var(--tx2,#374151); margin: 0; }
.qr-empty-sub   { font-size: 12.5px; color: var(--tx3,#9ca3af); text-align: center; }

/* Custom QR table */
.qr-list {
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 14px;
  overflow: hidden;
}
.qr-list-header {
  display: flex; align-items: center;
  padding: 10px 16px;
  background: var(--surface2, #f8fafc);
  border-bottom: 1px solid var(--border, #e2e8f0);
}
.qr-list-row {
  display: flex; align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border2, #f1f5f9);
  transition: background 0.15s;
}
.qr-list-row:last-child { border-bottom: none; }
.qr-list-row:hover { background: var(--surface2, #f8fafc); }

.qr-col { font-size: 13px; }
.qr-list-header .qr-col {
  font-size: 10.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--tx3, #9ca3af);
}
.qr-col-no      { width: 40px; flex-shrink: 0; }
.qr-col-date    { width: 120px; flex-shrink: 0; color: var(--tx2); }
.qr-col-area    { flex: 1; min-width: 0; font-weight: 600; color: var(--tx); }
.qr-col-scans   { width: 100px; flex-shrink: 0; color: var(--tx2); }
.qr-col-status  { width: 70px; flex-shrink: 0; }
.qr-col-actions { width: 140px; flex-shrink: 0; display: flex; justify-content: center; }

.qr-no-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 6px;
  background: var(--surface2, #f1f5f9);
  font-size: 11px; font-weight: 700; color: var(--tx3, #6b7280);
}
.action-group { display: flex; gap: 6px; align-items: center; justify-content: flex-end; }
.action-btn {
  position: relative; width: 32px; height: 32px; border-radius: 8px;
  background: transparent; border: none; display: inline-flex;
  align-items: center; justify-content: center; cursor: pointer;
  transition: all 0.2s; overflow: hidden;
}
.action-btn .hover-bg { position: absolute; inset: 0; background: transparent; transition: background 0.2s; border-radius: 8px; }
.action-btn:hover    .hover-bg { background: rgba(107,114,128,0.10); }
.action-edit:hover   .hover-bg { background: rgba( 59,130,246,0.12); }
.action-accept:hover .hover-bg { background: rgba( 16,185,129,0.12); }
.action-delete:hover .hover-bg { background: rgba(239, 68, 68,0.12); }

/* ── QR form ── */
.section-label { font-size: 11.5px; font-weight: 700; color: var(--tx3); text-transform: uppercase; letter-spacing: 0.05em; display: flex; align-items: center; gap: 8px; }
.section-label-hint { font-size: 10px; font-weight: 600; color: #9ca3af; background: var(--surface2,#f1f5f9); border-radius: 4px; padding: 1px 6px; text-transform: none; letter-spacing: 0; }
.time-slot-row { display: flex; align-items: center; background: var(--surface2,#f5f5f5); border-radius: 10px; padding: 10px 14px; margin-bottom: 8px; }
.slot-text { font-size: 13.5px; font-weight: 600; color: var(--tx); }
.slot-remove-btn { width: 26px; height: 26px; border-radius: 6px; border: 1px solid rgba(239,68,68,0.25); background: rgba(239,68,68,0.07); color: #DC2626; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
.add-slot-btn { width: 100%; height: 42px; border-radius: 10px; border: 1.5px dashed var(--border,#cbd5e1); background: transparent; color: var(--tx3); display: flex; align-items: center; justify-content: center; gap: 4px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.add-slot-btn:hover { border-color: var(--brand,#10b981); color: var(--brand,#10b981); }
.inline-picker-header { display: flex; align-items: center; padding: 4px 0 8px; border-bottom: 1px solid var(--border,#e2e8f0); margin-bottom: 4px; }
.inline-picker-title { font-size: 13.5px; font-weight: 700; color: var(--tx); }
.inline-picker-hint { font-size: 12.5px; color: var(--tx3); padding: 4px 0; }
.inline-picker-wrap { border-radius: 12px; overflow: hidden; border: 1px solid var(--border,#e2e8f0); }
.inline-time-picker { border-radius: 0 !important; box-shadow: none !important; }
</style>
